import { useEffect, useState } from 'react';
import { List } from '@refinedev/antd';
import { Table, Switch, App as AntApp, Card, Skeleton, Tag, Space } from 'antd';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

interface AmenityConfig {
  id: number;
  amenity: 'AIR_CONDITIONER' | 'MUSIC' | 'NO_SMOKING' | 'WOMEN_ONLY';
  isActive: boolean;
  sortOrder: number;
}

// Colour per amenity tag — purely cosmetic, mirrors the tariff list style.
const AMENITY_COLORS: Record<AmenityConfig['amenity'], string> = {
  AIR_CONDITIONER: 'blue',
  MUSIC: 'purple',
  NO_SMOKING: 'green',
  WOMEN_ONLY: 'magenta',
};

export const AmenityList = () => {
  const { message } = AntApp.useApp();
  const [rows, setRows] = useState<AmenityConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<AmenityConfig[]>('/admin/amenities');
      setRows(res.data);
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('amenities.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Toggle whether drivers are offered this amenity on the line creation form.
  const toggleActive = async (row: AmenityConfig, isActive: boolean) => {
    setBusyId(row.id);
    try {
      await apiClient.put(`/admin/amenities/${row.id}`, { isActive });
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, isActive } : r)),
      );
      message.success(t('amenities.saved'));
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('amenities.saveFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_: unknown, __: AmenityConfig, idx: number) => idx + 1,
    },
    {
      title: t('amenities.name'),
      dataIndex: 'amenity',
      key: 'amenity',
      render: (amenity: AmenityConfig['amenity']) => (
        <Space>
          <Tag color={AMENITY_COLORS[amenity]}>{t(`amenities.${amenity}`)}</Tag>
          <span style={{ color: '#7F8C8D', fontSize: 12 }}>{amenity}</span>
        </Space>
      ),
    },
    {
      title: t('amenities.available'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean, row: AmenityConfig) => (
        <Switch
          checked={isActive}
          loading={busyId === row.id}
          onChange={(v) => toggleActive(row, v)}
        />
      ),
    },
  ];

  return (
    <List title={t('amenities.title')}>
      <div style={{ color: '#7F8C8D', fontSize: 13, marginBottom: 12 }}>
        {t('amenities.hint')}
      </div>
      <Table
        dataSource={rows}
        columns={columns}
        rowKey="id"
        pagination={false}
        loading={
          loading
            ? {
                indicator: (
                  <Card bordered={false}>
                    <Skeleton active paragraph={{ rows: 3 }} />
                  </Card>
                ),
              }
            : false
        }
      />
    </List>
  );
};
